import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { getRequests, addRequest, subscribe } from '../../../services/requestStore';

const InformationRequests = () => {
  const { t, i18n } = useTranslation();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestType, setRequestType] = useState('');
  const [requestDetails, setRequestDetails] = useState('');
  const [successBanner, setSuccessBanner] = useState(false);

  const formatDateLocal = (date) => {
    const d = date instanceof Date ? date : new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return i18n.language === 'de' ? `${dd}.${mm}.${yyyy}` : `${dd}/${mm}/${yyyy}`;
  };

  // Read requests from the shared store and re-read on any change.
  // This way when the HR admin approves a request, this view updates live.
  const [requests, setRequests] = useState(() => getRequests(formatDateLocal));
  useEffect(() => {
    return subscribe(() => setRequests(getRequests(formatDateLocal)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const requestTypes = [
    { value: 'pay-data', label: t('employeePortal.requests.typePayDataExport') },
    { value: 'band-info', label: t('employeePortal.requests.typePayBandInfo') },
    { value: 'peer-comparison', label: t('employeePortal.requests.typePeerComparison') },
    { value: 'pay-history', label: t('employeePortal.requests.typePayHistory') },
    { value: 'benefits-info', label: t('employeePortal.requests.typeBenefitsInfo') },
    { value: 'other', label: t('employeePortal.requests.typeOther') }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10 border-success/20';
      case 'processing':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'pending':
        return 'text-muted-foreground bg-muted/30 border-border';
      default:
        return 'text-muted-foreground bg-muted/30 border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'processing': return 'Clock';
      case 'pending': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return t('employeePortal.requests.statusCompleted');
      case 'processing': return t('employeePortal.requests.statusProcessing');
      case 'pending': return t('employeePortal.requests.statusPending');
      default: return status;
    }
  };

  /**
   * handleDownload, generates a real Article 7 disclosure PDF on demand.
   * Uses dynamic import so the PDF library (~1.4MB) is only loaded when
   * actually needed, not on every employee-portal page view.
   */
  const handleDownload = async (request) => {
    const lang = i18n.language || 'en';
    try {
      const { downloadArticle7Disclosure } = await import('../../../pdf/Article7DisclosurePDF.jsx');
      // Pass the lang-formatted dates so the PDF reads naturally
      await downloadArticle7Disclosure(
        {
          ...request,
          requestDate: request.requestDate || formatDateLocal(new Date()),
          completedDate: request.completedDate || formatDateLocal(new Date()),
        },
        lang
      );
    } catch (err) {
      // PDF generation failure shouldn't block the demo, log and let the user
      // try again. In a real product we'd show an error toast here.
      console.error('PDF download failed', err);
    }
  };

  const handleSubmitRequest = (e) => {
    e?.preventDefault();
    const selected = requestTypes.find((rt) => rt.value === requestType);
    const today = formatDateLocal(new Date());
    addRequest({
      title: selected ? selected.label : t('employeePortal.requests.newRequestTitle'),
      description: requestDetails || t('employeePortal.requests.newRequestDescription'),
      requestDate: today,
    });
    // The subscribe() in useEffect will refresh the list automatically.
    setShowRequestForm(false);
    setRequestType('');
    setRequestDetails('');
    setSuccessBanner(true);
    // No auto-dismiss, the demo requires the banner stays visible long enough
    // for Pascal to walk over to the admin view. He can close it manually.
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {t('employeePortal.requests.cardTitleFull')}
        </h2>
        <Button
          variant="outline"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowRequestForm(!showRequestForm)}
        >
          {t('employeePortal.requests.newRequestBtn')}
        </Button>
      </div>

      {/* Success Banner, appears briefly after submitting */}
      {successBanner && (
        <div className="bg-success/10 border border-success/30 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Icon name="CheckCircle" size={20} className="text-success shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-foreground">
              {t('employeePortal.requests.submitSuccess')}
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('employeePortal.requests.submitSuccessBody')}
            </p>
          </div>
          <button
            onClick={() => setSuccessBanner(false)}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Dismiss"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
      )}

      {/* Request Form */}
      {showRequestForm && (
        <div className="bg-muted/20 border border-border rounded-lg p-4 mb-6">
          <h3 className="font-medium text-foreground mb-4">
            {t('employeePortal.requests.submitFormTitle')}
          </h3>
          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <Select
              label={t('employeePortal.requests.requestType')}
              placeholder={t('employeePortal.requests.requestTypePlaceholder')}
              options={requestTypes}
              value={requestType}
              onChange={setRequestType}
              required
            />

            <Input
              label={t('employeePortal.requests.additionalDetails')}
              type="text"
              placeholder={t('employeePortal.requests.detailsPlaceholder')}
              value={requestDetails}
              onChange={(e) => setRequestDetails(e?.target?.value)}
              description={t('employeePortal.requests.detailsHint')}
            />

            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t('employeePortal.requests.processingTitle')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('employeePortal.requests.processingBody')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button type="submit" disabled={!requestType}>
                {t('employeePortal.requests.submitRequest')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRequestForm(false)}
              >
                {t('employeePortal.requests.cancelBtn')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Recent Requests */}
      <div>
        <h3 className="font-medium text-foreground mb-4">
          {t('employeePortal.requests.recentRequests')}
        </h3>

        {requests?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{t('employeePortal.requests.noRequestsTitle')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('employeePortal.requests.noRequestsBody')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests?.map((request) => (
              <div key={request?.id} id={`request-${request?.id}`} className="border border-border rounded-lg p-4 transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{request?.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{request?.description}</p>
                  </div>
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(request?.status)}`}>
                    <Icon name={getStatusIcon(request?.status)} size={14} />
                    <span>{getStatusLabel(request?.status)}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>{t('employeePortal.requests.requestedOn', { date: request?.requestDate })}</span>
                    </div>
                    {request?.completedDate && (
                      <div className="flex items-center space-x-1">
                        <Icon name="CheckCircle" size={14} />
                        <span>{t('employeePortal.requests.completedOn', { date: request?.completedDate })}</span>
                      </div>
                    )}
                  </div>

                  {request?.status === 'completed' && request?.downloadUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => handleDownload(request)}
                    >
                      {t('employeePortal.requests.downloadBtn')}
                    </Button>
                  )}
                </div>

                {request?.estimatedCompletion && request?.status === 'processing' && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Icon name="Clock" size={14} />
                      <span>{t('employeePortal.requests.estimatedCompletion', { date: request?.estimatedCompletion })}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legal Information */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="bg-muted/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Scale" size={16} className="text-primary mt-0.5" />
            <div>
              <h4 className="font-medium text-foreground mb-2">
                {t('employeePortal.requests.rightsTitle')}
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• {t('employeePortal.requests.right1')}</li>
                <li>• {t('employeePortal.requests.right2')}</li>
                <li>• {t('employeePortal.requests.right3')}</li>
                <li>• {t('employeePortal.requests.right4')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InformationRequests;
